import React, { useEffect, useState, useCallback, useRef } from "react";
import styles from "./userRecordingsTable.module.css";
import { BASE_URL_DYNAMIC } from "../../services/api/base-query";
import { getCookie } from "../../utils/token";
import BackArrowButton from "../backArrowButton/backArrowButton";
import {
  TMeetingRecord,
  MeetingRecordStatus,
  ProcessingStages,
  ProcessingStageSteps,
  TProcessingStage,
} from "../../services/types/sessionSummary.types";
import { useGetUserMeetingRecordsMutation } from "../../services/api/sessionSummary.api";
import { tWithKeyAttr as t } from "../../i18nHelper";
import i18n from "../../i18n";
import { IUserReport } from "../../services/types/users.types";
import UserRecordingSummary from "../userRecordingSummary/userRecordingSummary";

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
}

function formatTime(
  dateStr: string | null | undefined,
  utcTime?: string | null,
): string {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "";
    const isMidnight =
      date.getUTCHours() === 0 &&
      date.getUTCMinutes() === 0 &&
      date.getUTCSeconds() === 0;
    if (!isMidnight) {
      const hours = String(date.getHours()).padStart(2, "0");
      const mins = String(date.getMinutes()).padStart(2, "0");
      return `${hours}:${mins}`;
    }
    if (utcTime) {
      const datePart = dateStr.split("T")[0];
      const combined = new Date(`${datePart}T${utcTime}:00Z`);
      if (!isNaN(combined.getTime())) {
        const hours = String(combined.getHours()).padStart(2, "0");
        const mins = String(combined.getMinutes()).padStart(2, "0");
        return `${hours}:${mins}`;
      }
    }
    return "";
  } catch {
    return "";
  }
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "";
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

function getCurrentStep(stage: TProcessingStage | null | undefined): number {
  if (!stage) return 0;
  return ProcessingStageSteps[stage] || 0;
}

function getStageLabel(stage: TProcessingStage | null | undefined): string {
  if (!stage) return "";
  const stageLabels: Record<string, string> = {
    [ProcessingStages.UPLOADING]: t("STAGE_UPLOADING"),
    [ProcessingStages.TRANSCRIBING]: t("STAGE_TRANSCRIBING"),
    [ProcessingStages.ANALYZING]: t("STAGE_ANALYZING"),
    [ProcessingStages.FINALIZING]: t("STAGE_FINALIZING"),
    [ProcessingStages.TEXT_ANALYSIS]: t("STAGE_ANALYZING"),
    [ProcessingStages.AUDIO_ANALYSIS]: t("STAGE_ANALYZING"),
    [ProcessingStages.SUMMARIZATION]: t("STAGE_ANALYZING"),
  };
  return stageLabels[stage] || stage;
}

function getStatusBadgeClass(status: string | undefined): string {
  switch (status) {
    case MeetingRecordStatus.COMPLETE:
      return styles.statusCompleted;
    case MeetingRecordStatus.DRAFT:
      return styles.statusDraft;
    case MeetingRecordStatus.PROCESSING:
      return styles.statusProcessing;
    case MeetingRecordStatus.FAILED:
      return styles.statusFailed;
    default:
      return styles.statusCompleted;
  }
}

function getStatusText(status: string | undefined): string {
  switch (status) {
    case MeetingRecordStatus.COMPLETE:
      return t("STATUS_COMPLETE");
    case MeetingRecordStatus.DRAFT:
      return t("STATUS_DRAFT");
    case MeetingRecordStatus.PROCESSING:
      return t("STATUS_PROCESSING");
    case MeetingRecordStatus.FAILED:
      return t("STATUS_FAILED");
    default:
      return t("STATUS_COMPLETE");
  }
}

interface UserRecordingsTableProps {
  user: IUserReport;
  onClose: () => void;
}

export default function UserRecordingsTable({
  user,
  onClose,
}: UserRecordingsTableProps) {
  const [filter, setFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedRecord, setSelectedRecord] = useState<TMeetingRecord | null>(null);

  const userGuid = user.Users.Guid;
  const userId = user.Users.Id;
  const userName =
    `${user.Users.FirstName || ""} ${user.Users.LastName || ""}`.trim() ||
    "-";

  const [allRecords] = useGetUserMeetingRecordsMutation();
  const [data, setData] = useState<TMeetingRecord[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const [playingRecordNumber, setPlayingRecordNumber] = useState<number | null>(
    null,
  );
  const [loadingAudioRecord, setLoadingAudioRecord] = useState<number | null>(
    null,
  );

  const toggleAudioPlayback = useCallback(
    async (recordNumber: number, audioUrl: string) => {
      if (playingRecordNumber === recordNumber && audioRef.current) {
        audioRef.current.pause();
        setPlayingRecordNumber(null);
        return;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      const resolvedUrl = audioUrl.startsWith("/")
        ? `${BASE_URL_DYNAMIC.replace(/\/$/, "")}${audioUrl}`
        : audioUrl;

      setLoadingAudioRecord(recordNumber);
      try {
        const token = getCookie("accessToken");
        const resp = await fetch(resolvedUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        const blobUrl = URL.createObjectURL(blob);
        blobUrlRef.current = blobUrl;

        const audio = new Audio(blobUrl);
        audio.onended = () => setPlayingRecordNumber(null);
        audio.onerror = () => setPlayingRecordNumber(null);
        await audio.play();
        audioRef.current = audio;
        setPlayingRecordNumber(recordNumber);
      } catch {
        setPlayingRecordNumber(null);
      } finally {
        setLoadingAudioRecord(null);
      }
    },
    [playingRecordNumber],
  );

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  const fetchRecords = useCallback(() => {
    if (!userGuid) return;
    allRecords(userGuid)
      .then((response) => {
        if (
          "data" in response &&
          response.data?.records &&
          Array.isArray(response.data.records)
        ) {
          const mappedData: TMeetingRecord[] = response.data.records.map(
            (item: any) => ({
              audioAnalyze: item.audioAnalyze,
              date: item.date,
              meetingSummary: item.meetingSummary,
              optionsForReport: item.optionsForReport,
              recordNumber: item.recordNumber,
              textAnalyze: item.textAnalyze,
              therapistId: item.therapistId,
              therapistName: item.therapistName,
              transcription: item.transcription,
              type: item.type,
              userId: item.userId,
              videoAnalyze: item.faceAnalyze,
              duration: item.duration,
              status: item.status || MeetingRecordStatus.COMPLETE,
              time: item.time,
              subject: item.subject,
              completedAt: item.completedAt,
              processingStage: item.processingStage,
              errorMessage: item.errorMessage,
              retryCount: item.retryCount || 0,
              audioFileUrl: item.audioFileUrl || null,
              failedAtStage: item.failedAtStage || null,
            }),
          );
          setData(mappedData);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [userGuid, allRecords]);

  const hasProcessingRecords =
    data?.some((r) => r.status === MeetingRecordStatus.PROCESSING) ?? false;

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    if (!hasProcessingRecords) return;
    const pollInterval = setInterval(() => {
      fetchRecords();
    }, 5000);
    return () => clearInterval(pollInterval);
  }, [hasProcessingRecords, fetchRecords]);

  const now = new Date();
  const filtered = data?.filter((r) => {
    // Filter out failed recordings
    if (r.status === MeetingRecordStatus.FAILED) return false;
    const matchesSearch =
      r.type.includes(filter) ||
      r.date.includes(filter) ||
      String(r.userId).includes(filter) ||
      (r.subject && r.subject.includes(filter));
    if (!matchesSearch) return false;
    if (activeFilter === "all") return true;
    const recordDate = new Date(r.date);
    if (activeFilter === "month") {
      const oneMonthAgo = new Date(now);
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      return recordDate >= oneMonthAgo;
    }
    if (activeFilter === "3months") {
      const threeMonthsAgo = new Date(now);
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      return recordDate >= threeMonthsAgo;
    }
    if (activeFilter === "year") {
      const oneYearAgo = new Date(now);
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      return recordDate >= oneYearAgo;
    }
    return true;
  });

  const totalRecordings = data?.filter(r => r.status !== MeetingRecordStatus.FAILED).length || 0;
  const lang = i18n.language;

  if (selectedRecord) {
    return (
      <UserRecordingSummary
        record={selectedRecord}
        userGuid={userGuid}
        onClose={() => setSelectedRecord(null)}
      />
    );
  }

  return (
    <div className={styles.rtContainer} dir={i18n.dir()}>
      <div className={styles.rtTopbar}>
        <BackArrowButton
          text={t("PREVIOUS_SCREEN")}
          onClick={onClose}
          position={"right"}
        />
      </div>

      <div className={styles.patientInfo}>
        <div className={styles.patientHeader}>
          <div className={styles.patientAvatar}>
            <span role="img" aria-label="user">
              👤
            </span>
          </div>
          <div className={styles.patientName}>
            <h2>{userName}</h2>
            <div className={styles.patientId}>
              {t("PATIENT_FILE_NUMBER")}: PT-{userId || "---"}
            </div>
          </div>
        </div>
        <div className={styles.patientDetails}>
          <div className={styles.detailItem}>
            <div className={styles.detailIcon}>📊</div>
            <div className={styles.detailContent}>
              <div className={styles.detailLabel}>
                {t("RECORDINGS_TOTAL_MEETINGS")}
              </div>
              <div className={styles.detailValue}>{totalRecordings}</div>
            </div>
          </div>
          <div className={styles.detailItem}>
            <div className={styles.detailIcon}>🎙️</div>
            <div className={styles.detailContent}>
              <div className={styles.detailLabel}>{t("RECORDINGS_TOTAL")}</div>
              <div className={styles.detailValue}>{totalRecordings}</div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.recordingsSection}>
        <div className={styles.sectionHeader}>
          <h2>📋 {t("RECORDINGS_LIST")}</h2>
          <div className={styles.filterButtons}>
            <button
              className={`${styles.filterBtn} ${activeFilter === "all" ? styles.filterBtnActive : ""}`}
              onClick={() => setActiveFilter("all")}
            >
              {t("FILTER_ALL")}
            </button>
            <button
              className={`${styles.filterBtn} ${activeFilter === "month" ? styles.filterBtnActive : ""}`}
              onClick={() => setActiveFilter("month")}
            >
              {t("FILTER_LAST_MONTH")}
            </button>
            <button
              className={`${styles.filterBtn} ${activeFilter === "3months" ? styles.filterBtnActive : ""}`}
              onClick={() => setActiveFilter("3months")}
            >
              {t("FILTER_3_MONTHS")}
            </button>
            <button
              className={`${styles.filterBtn} ${activeFilter === "year" ? styles.filterBtnActive : ""}`}
              onClick={() => setActiveFilter("year")}
            >
              {t("FILTER_YEAR")}
            </button>
          </div>
        </div>

        <div className={styles.Filter}>
          <div className={styles.searchFilter}>
            <span className={styles.searchIcon}>🔍</span>
            <input
              className={styles.searchInput}
              type="text"
              placeholder={t("FILTER")}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        </div>

        <table className={styles.recordingsTable}>
          <thead>
            <tr>
              <th>{t("RECORDINGS_DATE")}</th>
              <th>{t("RECORDINGS_TIME")}</th>
              <th>{t("RECORDINGS_DURATION")}</th>
              <th>{t("RECORDING_TYPE")}</th>
              <th>{t("RECORDINGS_SUBJECT")}</th>
              <th>{t("RECORDINGS_STATUS")}</th>
              <th>{t("ACTIONS")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered && filtered.length > 0 ? (
              filtered.map((r) => {
                const isProcessing =
                  r.status === MeetingRecordStatus.PROCESSING;
                const isFailed = r.status === MeetingRecordStatus.FAILED;
                const currentStep = getCurrentStep(r.processingStage);
                const totalSteps = 4;
                const type = lang === "he" ? r.type : "Audio";

                return (
                  <tr
                    key={r.recordNumber}
                    className={isProcessing ? styles.processingRow : ""}
                  >
                    <td data-label={t("RECORDINGS_DATE")}>
                      {formatDate(r.date)}
                    </td>
                    <td data-label={t("RECORDINGS_TIME")}>
                      {formatTime(r.date, r.time) || "—"}
                    </td>
                    <td data-label={t("RECORDINGS_DURATION")}>
                      {r.duration ? (
                        <span className={styles.durationBadge}>
                          {formatDuration(r.duration)}
                        </span>
                      ) : (
                        <span>—</span>
                      )}
                    </td>
                    <td data-label={t("RECORDING_TYPE")}>{type}</td>
                    <td
                      data-label={t("RECORDINGS_SUBJECT")}
                      className={styles.subjectCell}
                      title={r.subject || ""}
                    >
                      {r.subject || "—"}
                    </td>
                    <td data-label={t("RECORDINGS_STATUS")}>
                      {isProcessing ? (
                        <div className={styles.processingStatus}>
                          <div className={styles.stepper}>
                            {[1, 2, 3, 4, 5, 6].map((step) => (
                              <div
                                key={step}
                                className={`${styles.stepDot} ${step <= currentStep ? styles.stepDotActive : ""} ${step === currentStep ? styles.stepDotCurrent : ""}`}
                                title={
                                  step <= currentStep
                                    ? getStageLabel(r.processingStage)
                                    : ""
                                }
                              />
                            ))}
                          </div>
                          <span className={styles.processingText}>
                            {getStageLabel(r.processingStage)} ({currentStep}/
                            {totalSteps})
                          </span>
                        </div>
                      ) : isFailed ? (
                        <div className={styles.failedStatus}>
                          <span
                            className={`${styles.statusBadge} ${styles.statusFailed}`}
                          >
                            {t("STATUS_FAILED")}
                          </span>
                          {r.errorMessage && (
                            <span
                              className={styles.errorTooltip}
                              title={r.errorMessage}
                            >
                              ⚠️
                            </span>
                          )}
                        </div>
                      ) : (
                        <span
                          className={`${styles.statusBadge} ${getStatusBadgeClass(r.status)}`}
                        >
                          {getStatusText(r.status)}
                        </span>
                      )}
                    </td>
                    <td className={styles.actionsCell}>
                      <button
                        className={styles.viewBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRecord(r);
                        }}
                      >
                        {t("VIEW_DETAILS")}
                      </button>
                      {r.audioFileUrl ? (
                        <button
                          className={styles.playBtn}
                          disabled={loadingAudioRecord === r.recordNumber}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAudioPlayback(
                              r.recordNumber,
                              r.audioFileUrl!,
                            );
                          }}
                          aria-label={
                            playingRecordNumber === r.recordNumber
                              ? t("PENDING_RECORDING_PAUSE")
                              : t("PENDING_RECORDING_PLAY")
                          }
                          title={
                            playingRecordNumber === r.recordNumber
                              ? t("PENDING_RECORDING_PAUSE")
                              : t("PENDING_RECORDING_PLAY")
                          }
                        >
                          {loadingAudioRecord === r.recordNumber
                            ? "⏳"
                            : playingRecordNumber === r.recordNumber
                              ? "⏸"
                              : "▶"}
                        </button>
                      ) : r.status === "complete" ? (
                        <button
                          className={`${styles.playBtn} ${styles.playBtnDisabled}`}
                          disabled
                          aria-label={t("RECORDING_DELETED_BY_POLICY")}
                          title={t("RECORDING_DELETED_BY_POLICY")}
                        >
                          ▶
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={7}
                  style={{ textAlign: "center", padding: "40px" }}
                >
                  {isLoading && !data?.length ? (
                    <div className={styles.loadingContainer}>
                      <div className={styles.loadingSpinner}></div>
                      <span>{t("LOADING")}</span>
                    </div>
                  ) : (
                    t("NO_RECORDINGS_FOUND")
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
